import get from 'lodash/get';
import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Mousetrap from 'mousetrap';
import { Layout } from 'blaze-validator';

import { Col, Nav, Tab } from 'react-bootstrap';

import { withFormContext } from './FormContext';
import ReadOnly from './ReadOnly';
import FormEditSteps from './FormEditSteps';
import FormTable from './FormTable';
import FormSection from './FormSection';
import FormControlBar from './FormControlBar';

import './styles/formedit.scss';
import ClickToEditOverlay from './ClickToEditOverlay';
import HighlightOverlay from './HighlightOverlay';
import ControlWrapper from './ControlWrapper';

class FormEdit extends Component {
  constructor(props) {
    super(props);

    this.nextTab = this.nextTab.bind(this);
    this.prevTab = this.prevTab.bind(this);
    this.handleTabSelect = this.handleTabSelect.bind(this);
    this.onHighlight = this.onHighlight.bind(this);
    this.clearHighlight = this.clearHighlight.bind(this);

    const { sticky, ...rest } = props.manager.layout.getTabLayouts();

    this.tabLayouts = rest;
    this.stickyLayout = sticky || {};

    this.state = {
      showHelp: props.showHelp,
      activeTabIndex: 0,
      highlight: '',
    };

    this.formEditRef = React.createRef();
  }

  componentDidMount() {
    const { readOnly, persistTabs, originalRender, context } = this.props;

    context.ee.on('toggleHelp', this.consume.bind(this));
    Mousetrap.bind(['right'], this.nextTab);
    Mousetrap.bind(['left'], this.prevTab);

    // Focus first input/select control when mounted
    if (!readOnly && originalRender) {
      this.focusFirstField();
    }

    // Jump to tab if it's defined in the URL
    if (persistTabs && window.location.hash) {
      const callback = originalRender ? null : () => {};
      this.handleTabSelect(window.location.hash.substring(1), callback);
    }

    this.formEditRef.current.addEventListener(
      'keyup',
      (e) => {
        if (e.keyCode === 27) {
          // document.body.focus();
          const el = this.formEditRef.current.querySelector(':focus');
          el && el.blur(); // eslint-disable-line
        }
      },
      true
    );
  }

  componentWillUnmount() {
    this.props.context.ee.off('toggleHelp');
    Mousetrap.unbind(['right']);
    Mousetrap.unbind(['left']);
  }

  onHighlight(fieldName) {
    const { form, manager } = this.props;

    if (!form[fieldName]) {
      console.warn('Invalid fieldname', fieldName);
      return;
    }

    // Focus Field
    let focus = () => {};
    const { identifier } = form[fieldName];
    if (!this.formEditRef || !this.formEditRef.current.querySelector(`#${identifier}`)) {
      console.warn('Could not find', identifier);
    } else {
      focus = () => {
        this.formEditRef.current.querySelector(`#${identifier}`).focus();
      };
    }

    this.setState({ highlight: fieldName });

    // Find Tab
    const tabs = this.getVisibleTabs();
    if (tabs.length) {
      tabs.forEach((tabName, tabIndex) => {
        const tabFields = manager.layout.tabs[tabName];
        if (tabFields.includes(fieldName)) {
          this.changeTab(tabIndex, focus);
        }
      });
    } else {
      focus();
    }
  }

  /**
   * Should be attached as an event listener when a tab is to be changed.
   *
   * @param {String} tabKey - Unique name given for a tab.
   * @return
   */
  handleTabSelect(tabKey, callback) {
    const tabs = this.getVisibleTabs();

    let newIndex = 0;
    tabs.forEach((tabName, index) => {
      if (Layout.sanitizeKey(tabName) === tabKey) {
        newIndex = index;
      }
    });

    this.changeTab(newIndex, callback);
  }

  /**
   * Basically determines where which method should render the given 'field'
   * Currently the layout is a list of objects or arrays.  Arrays with more than
   * one object are a collection.  These need to be sent to the collection component
   *
   * @param {Mixed} field - field object or an array of objects.
   * @return {Object} JSX.
   */
  getControl(field) {
    // eslint-disable-line
    // if (Array.isArray(field) && field.length > 1) {
    //   return this.renderTable(field);
    // }

    if (field.type === 'table') {
      return this.renderTable(field);
    }

    if (field.type === 'element') {
      return this.renderElement(field);
    }

    return this.renderControl(field);
  }

  /**
   * Determine if there is anything invalid for a given tab.
   *
   * @param {String} tabName - Unique name given for a tab.
   * @return {Boolean} - Status of tab.
   */
  getTabStatus(tabId) {
    const { form, manager } = this.props;
    let valid = true;

    const fieldsInTab = manager.layout.getAllFieldsUnderGroup(tabId);

    fieldsInTab.forEach((fieldName) => {
      const field = form[fieldName];

      // Check if field has an array of data
      if (Array.isArray(field.data)) {
        field.data.forEach((d, index) => {
          if (
            field.validIndex[index] === false &&
            Array.isArray(field.touchedIndex) &&
            field.touchedIndex[index] === true
          ) {
            valid = false;
          }
        });
      } else {
        if (field.valid === false && field.touched === true) {
          valid = false;
        }
      }
    });

    return valid;
  }

  /**
   * Lookup to get tabKey by tabIndex
   *
   * @param {Number} index - tabIndex.
   * @return {String} - tabKey.
   */
  getTabKeyByIndex(index) {
    const tabs = this.getVisibleTabs();
    return Layout.sanitizeKey(tabs[index]);
  }

  getVisibleTabs() {
    const { form, manager, currentStep, itemId } = this.props;
    const visibleTabs = [];

    manager.layout.forEachTab((tabName, config) => {
      const fieldsInTab = manager.layout.getAllFieldsUnderGroup(tabName);

      if (manager.layout.hasSteps) {
        const { key } = manager.layout.steps[currentStep];
        if (key !== config.insideStep) {
          return;
        }
      }

      if (config.hidden !== undefined && config.hidden === true) {
        return;
      }

      let isHidden = true;
      // Loop through the fields in each tab
      fieldsInTab.forEach((fieldName) => {
        // Item Id field is handled elsewhere
        if (itemId && itemId.name && fieldName === itemId.name) {
          return;
        }

        if (form[fieldName].hidden === false) {
          isHidden = false;
        }
      });

      if (!isHidden && config.label !== 'sticky') {
        visibleTabs.push(config.label);
      }
    });

    return visibleTabs;
  }

  /**
   * Focuses the first field of the form.  If a tabIndex is given, it'll focus
   * the first non-disabled field on that tab.
   *
   * @param {Number} tabIndex - Focus a field on a particular tab.
   * @return
   */
  focusFirstField(tabIndex) {
    if (!this.props.context.focusFirstField) {
      return;
    }

    // First field for entire form
    let selector = '.control-wrapper.is-enabled .form-control:first-of-type';

    // First field for a given tab
    if (tabIndex !== undefined) {
      selector = `.tab-content div.tab-pane:nth-of-type(${
        tabIndex + 1
      }) .control-wrapper.is-enabled .form-control:first-of-type`;
    }

    if (this.formEditRef.current && this.formEditRef.current.querySelector(selector)) {
      this.formEditRef.current.querySelector(selector).focus();
    }
  }

  /**
   * Goto the next tab relative to currently active tab.
   * @return
   */
  nextTab() {
    const tabs = Object.keys(this.tabLayouts);

    let newIndex = this.state.activeTabIndex + 1;

    if (newIndex >= tabs.length) {
      newIndex = 0;
    }

    this.changeTab(newIndex);
  }

  /**
   * Goto the previous tab relative to currently active tab.
   * @return
   */
  prevTab() {
    const tabs = Object.keys(this.tabLayouts);

    let newIndex = this.state.activeTabIndex - 1;

    if (newIndex === -1) {
      newIndex = tabs.length - 1;
    }

    this.changeTab(newIndex);
  }

  /**
   * Goto a particular tab by tabIndex.
   * @return
   */
  changeTab(tabIndex, callback) {
    const { persistTabs, readOnly } = this.props;

    if (!callback) {
      callback = () => {
        if (!readOnly) {
          this.focusFirstField(tabIndex);
        }
      };
    }

    this.setState({ activeTabIndex: tabIndex }, () => {
      if (persistTabs) {
        window.history.replaceState({}, null, `#${this.getTabKeyByIndex(tabIndex)}`);
      }

      /*
        Sometimes focusing the first field doesn't work if animation is still
        transitioning, so a delay is needed
      */
      setTimeout(callback, 350);
    });
  }

  clearHighlight(fieldName) {
    if (this.state.highlight === fieldName) {
      this.setState({ highlight: '' });
    }
  }

  /**
   * Determines if a given section should be hidden by examining the meta data
   * of all the fields in that section.
   *
   * @param {String} section - The section name.
   * @return {Boolean} - Hidden or not.
   */
  isSectionHidden(sectionName, hidden) {
    const { form, manager } = this.props;

    // TODO This should only use the prop going forward
    if (hidden !== undefined) {
      return hidden;
    }

    const fields = manager.layout.getAllFieldsUnderGroup(sectionName);

    // TODO For now sections without fields will just be shown
    if (!fields.length) {
      return false;
    }

    return fields.every((fieldName) => form[fieldName].hidden === true);
  }

  /**
   * Consume any subscriptions set up with pubsub.
   *
   * @param {String} msg - Event topic.
   * @param {Object} data - Event payload.
   * @return
   */
  consume(data) {
    if (data.value !== this.state.showHelp) {
      this.setState({ showHelp: data.value });
    }
  }

  renderElement(field) {
    const { form } = this.props;
    const key = form[field.name].identifier;

    if (field.control === 'hr') {
      return <hr key={key} className={field.className} style={field.style} />;
    }

    return <div />;
  }

  /**
   * Renders a particular control based on the field object.
   *
   * @param {Object} field - The field object defined a page layout.
   * @return {Object} - JSX.
   */
  renderControl(field) {
    const { form, context } = this.props;
    const { col } = field;

    const controlId = field.name === 'itemid' ? 'itemid' : form[field.name].identifier;

    const ControlComponent = context.manager.controls[form[field.name].control.toLowerCase()];
    return (
      <ControlWrapper
        col={col}
        controlId={controlId}
        field={form[field.name]}
        key={controlId}
        onClick={this.props.onWrapperClick}
      >
        <ControlComponent
          control={form[field.name].control}
          controlId={controlId}
          disabled={form[field.name].disabled}
          errorMessage={form[field.name].validError}
          field={form[field.name]}
          onBlur={this.props.onBlur}
          onChange={this.props.onChange}
          onClick={this.props.onClick}
          onTagChange={this.props.onTagChange}
          setInfo={this.props.setInfo}
          setWarning={this.props.setWarning}
          hidden={form[field.name].hidden}
          key={form[field.name].identifier}
          options={form[field.name].options}
          originalValue={form[field.name].originalDisplayValue}
          style={form[field.name].style}
          touched={form[field.name].touched}
          search={form[field.name].validation}
          valid={form[field.name].valid}
          validIndex={form[field.name].validIndex}
          value={form[field.name].displayValue}
          warning={form[field.name].warning}
          info={form[field.name].info}
          calculated={form[field.name].calculated}
          {...field.props}
        />
        <ClickToEditOverlay />
        <HighlightOverlay clearHighlight={this.clearHighlight} fieldName={field.name} />
      </ControlWrapper>
    );
  }

  /**
   * Renders a table that contains a list of controls.
   *
   * @param {Array} collection - List of field configs.
   * @return {Object} - JSX.
   */
  renderTable(collection) {
    const { manager } = this.props;

    const { key, fields } = collection;

    const {
      canAdd,
      canBatchAdd,
      canDelete,
      canInsert,
      canSort,
      col,
      collapsibleRows,
      collapsibleText,
      hidden,
      hideOnEmpty,
      initialSize,
      label,
      limit,
      maxListSize,
      minListSize,
      numberedRows,
      repeatHeaders,
      threshold,
    } = manager.layout.lookup[key];

    const form = {};

    manager.layout.getAllFieldsUnderGroup(key).forEach((fieldName) => {
      const field = this.props.form[fieldName];
      form[fieldName] = field;
    });

    const xxlValue = (col && (col.xxl || col.xl || col.lg || col.md)) || 12;

    return (
      <Col
        key={key}
        // xxl={{ span: (col && (col.xxl || col.xl || col.lg || col.md)) || 12, offset: col && col.xxlOffset }}
        xl={{ span: (col && (col.xl || col.lg || col.md)) || 12, offset: col && col.xlOffset }}
        lg={{ span: (col && (col.lg || col.md)) || 12, offset: col && col.lgOffset }}
        md={{ span: (col && (col.md || col.sm)) || 12, offset: col && col.mdOffset }}
        sm={{ span: (col && col.sm) || 12, offset: col && col.smOffset }}
        xs={{ span: (col && col.xs) || 12, offset: col && col.xsOffset }}
        className={`col-xxl-${xxlValue}`}
      >
        <FormTable
          key={key}
          canAdd={canAdd}
          canBatchAdd={canBatchAdd}
          canDelete={canDelete}
          canInsert={canInsert}
          canSort={canSort}
          checkAll={this.props.checkAll}
          collapsibleRows={collapsibleRows}
          collapsibleText={collapsibleText}
          collection={fields}
          controlId={key}
          form={form}
          hidden={hidden}
          hideOnEmpty={hideOnEmpty}
          initialSize={initialSize}
          limit={limit}
          maxSize={maxListSize}
          minSize={minListSize}
          numberedRows={numberedRows}
          onBlur={this.props.onBlur}
          onChange={this.props.onChange}
          onClick={this.props.onClick}
          onRemoveItem={this.props.onRemoveItem}
          onRowChange={this.props.onRowChange}
          repeatHeaders={repeatHeaders}
          setInfo={this.props.setInfo}
          setWarning={this.props.setWarning}
          threshold={threshold}
          title={label}
        />
      </Col>
    );
  }

  /**
   * Entry point to rendering a form of a layout.  If unique tabs (pages) exist
   * they will be split into separate layouts.
   *
   * @param {Array} layout - Page layout.
   * @return {Object} - JSX.
   */
  renderForm(layout = [], tabName = '') {
    const { readOnly, manager, itemId } = this.props;

    const crawlLayout = (layout = [], parent = {}) => {
      const renderedFields = [];

      layout.forEach((field) => {
        // Item Id field is handled elsewhere
        if (itemId && itemId.name && field.name === itemId.name) {
          return;
        }

        if (field.type === 'section') {
          const { fields, ...config } = field;
          const section = crawlLayout(fields, config);
          renderedFields.push(section);
        } else {
          renderedFields.push(this.getControl(field));
        }

        const identifier = field.type === 'table' ? field.key : field.identifier;

        if (get(field, 'col.clear')) {
          renderedFields.push(<div className="clearfix" key={`${identifier}-clear`} />);
        }
      });

      if (parent.type === 'section') {
        const { key } = parent;
        const { col, hidden, label, small, struct, labelAlign, style } = manager.layout.lookup[key];
        const hide = this.isSectionHidden(key, hidden);

        return (
          <FormSection
            title={label}
            controlId={key}
            key={key}
            col={col}
            hide={hide}
            small={small}
            struct={struct}
            labelAlign={labelAlign}
            style={style}
          >
            {renderedFields}
            <div className="clearfix" />
          </FormSection>
        );
      }

      return renderedFields;
    };

    const renderedForm = crawlLayout(layout);

    const className = classNames('rendered-form', {
      'is-sticky': tabName === 'sticky',
    });

    if (!renderedForm.length) {
      return null;
    }

    if (readOnly) {
      return (
        <ReadOnly hideIcon={tabName === 'sticky'}>
          <div className={className}>
            {renderedForm}
            <div className="clearfix" />
          </div>
        </ReadOnly>
      );
    }

    return (
      <div className={className}>
        {renderedForm}
        <div className="clearfix" />
      </div>
    );
  }

  /**
   * Calls renderForm() with the appropriate tab wrapper.
   *
   * @return {Object} - JSX.
   */
  renderTabs() {
    const { options, manager, currentStep } = this.props;

    // Check for steps
    // if (manager.layout.hasSteps) {
    //   return this.renderForm(manager.layout.getSteps()[currentStep].fields);
    // }

    const visibleTabs = this.getVisibleTabs();

    // No tabs defined at all
    if (visibleTabs.length === 0) {
      if (manager.layout.hasSteps) {
        const { fields } = manager.layout.steps[currentStep];
        return this.renderForm(fields);
      }
      const { fields } = this.tabLayouts.Main;
      return this.renderForm(fields);
    }

    // One tab renders no tabs
    if (visibleTabs.length === 1) {
      const { fields } = this.tabLayouts[visibleTabs[0]];
      return this.renderForm(fields);
    }

    // Handle multiple tabs
    const tabsClassName = classNames({
      [`tabs-${visibleTabs.length}`]: visibleTabs.length <= 5,
      'tabs-many': visibleTabs.length > 5,
      vertical: options.verticalTabs === true,
    });

    const tabs = [];
    const content = [];

    visibleTabs.forEach((tabName) => {
      const { fields } = this.tabLayouts[tabName];

      const controlId = `tab-${Layout.sanitizeKey(tabName)}`;
      const tempName = Layout.sanitizeKey(tabName); // TODO unnecessary
      const tabStatus = this.getTabStatus(controlId);
      const tabConfig = manager.layout.lookup[controlId];
      let tabLabel = tabConfig.label;

      // Tab with a warning
      if (!tabStatus) {
        tabLabel = (
          <div className="warning-tab">
            <i className="zmdi zmdi-alert-triangle" /> {tabConfig.label}
          </div>
        );
      }

      tabs.push(
        <Nav.Item key={tempName} className={tabConfig.style ? tabConfig.style : ''}>
          <Nav.Link eventKey={tempName}>{tabLabel}</Nav.Link>
        </Nav.Item>
      );

      // Set col layout for tab
      const col = tabConfig.col || { xs: 12, sm: 12, md: 12, lg: 12 };
      const xxlValue = (col && (col.xxl || col.xl || col.lg || col.md)) || 12;

      content.push(
        <Tab.Pane key={tempName} eventKey={tempName} className="warning">
          <Col
            // xxl={{ span: (col && (col.xxl || col.xl || col.lg || col.md)) || 12, offset: col && col.xxlOffset }}
            xl={{ span: (col && (col.xl || col.lg || col.md)) || 12, offset: col && col.xlOffset }}
            lg={{ span: (col && (col.lg || col.md)) || 12, offset: col && col.lgOffset }}
            md={{ span: (col && (col.md || col.sm)) || 12, offset: col && col.mdOffset }}
            sm={{ span: (col && col.sm) || 12, offset: col && col.smOffset }}
            xs={{ span: (col && col.xs) || 12, offset: col && col.xsOffset }}
            className={classNames('form-tab-content', `col-xxl-${xxlValue}`)}
          >
            {this.renderForm(fields)}
          </Col>
        </Tab.Pane>
      );
    });

    return (
      <div id="page-tabs" className={tabsClassName}>
        <Tab.Container
          id="page-tabs"
          onSelect={(tabKey) => this.handleTabSelect(tabKey)}
          defaultActiveKey={this.getTabKeyByIndex(this.state.activeTabIndex)}
          activeKey={this.getTabKeyByIndex(this.state.activeTabIndex)}
        >
          <div className="tab-labels">
            <Nav variant="tabs">{tabs}</Nav>
          </div>
          <Tab.Content>{content}</Tab.Content>
        </Tab.Container>
      </div>
    );
  }

  /**
   * An id field is rendered when appropriate.  Normally an id field is only
   * entered or modified if it's a new entry in a list.
   *
   * @return {Object} - JSX.
   */
  renderIdField() {
    const { itemId, form } = this.props;

    if (!itemId || !form[itemId.name]) {
      return null;
    }

    const className = classNames('rendered-form', 'item-id', { hidden: form[itemId.name].hidden });

    return <div className={className}>{this.getControl(form[itemId.name])}</div>;
  }

  /**
   * Render the toolbar for the form.  The default buttons are Submit, Reset, and Cancel
   *
   * @return {Object} - JSX.
   */
  renderControls() {
    const {
      readOnly,
      saving,
      cancelLabel,
      customActions,
      report,
      form,
      steps,
      currentStep,
      previousStep,
      context,
      onResetForm,
      onSubmit,
      onCancel,
      submitLabel,
    } = this.props;
    const { hideToolbar, onValidationReport } = context;

    if (hideToolbar) {
      return null;
    }

    const hasSteps = !!steps.length;
    const lastStep = hasSteps && currentStep === steps.length - 1;
    const firstStep = hasSteps && currentStep === 0;

    const handleValidationReport = onValidationReport
      ? () => onValidationReport({ report, form, highlight: this.onHighlight })
      : null;

    return (
      <FormControlBar
        cancelLabel={cancelLabel}
        customActions={customActions}
        firstStep={firstStep}
        hasSteps={hasSteps}
        lastStep={lastStep}
        onCancel={onCancel}
        onResetForm={onResetForm}
        onSubmit={onSubmit}
        onValidationReport={handleValidationReport}
        previousStep={previousStep}
        readOnly={readOnly}
        saving={saving}
        submitLabel={submitLabel}
        validationIssues={report.valid === false ? Object.keys(report.failed).length : 0}
      />
    );
  }

  render() {
    const { onSubmit, saving, currentStep, steps, dirty, showHelp, options, onFormClick, originalRender } = this.props;

    const hasSteps = steps.length !== 0;
    const lastStep = hasSteps && currentStep === steps.length - 1;

    const className = classNames(
      'form-edit-container',
      this.props.className,
      {
        fadeIn: originalRender,
        animated: originalRender,
        'has-help': showHelp,
        'is-saving': saving,
        'is-dirty': dirty,
        contained: options.contained === true,
      },
      hasSteps && `step-${currentStep + 1}`,
      lastStep && 'last-step'
    );

    return (
      <div className={className} ref={this.formEditRef}>
        {hasSteps && (
          <FormEditSteps currentStep={currentStep} steps={steps.map((s) => s.label)} jump={this.props.previousStep} />
        )}
        <form onSubmit={onSubmit} onClick={onFormClick}>
          <div className="form-wrapper">
            {this.renderIdField()}
            {this.renderForm(this.stickyLayout.fields, 'sticky')}
            {this.renderTabs()}
            {this.props.children}
          </div>
          {this.renderControls()}
        </form>
      </div>
    );
  }
}

FormEdit.defaultProps = {
  cancelLabel: 'Cancel',
  checkAll: () => {},
  children: null,
  className: '',
  currentStep: null,
  customActions: [],
  dirty: false,
  form: {},
  hideReset: true,
  itemId: null,
  layout: [],
  onBlur: () => {},
  onCancel: () => {},
  onChange: () => {},
  onClick: () => {},
  onFormClick: () => {},
  onRemoveItem: () => {},
  onResetForm: () => {},
  onRowChange: () => {},
  onSubmit: () => {},
  onTagChange: () => {},
  options: {},
  persistTabs: true,
  previousStep: () => {},
  readOnly: false,
  report: {},
  setInfo: () => {},
  setWarning: () => {},
  showHelp: false,
  saving: false,
  submitLabel: 'Submit',
  steps: [],
};

FormEdit.propTypes = {
  cancelLabel: PropTypes.string,
  className: PropTypes.string,
  checkAll: PropTypes.func,
  children: PropTypes.node,
  context: PropTypes.object.isRequired,
  currentStep: PropTypes.number,
  customActions: PropTypes.array,
  dirty: PropTypes.bool,
  form: PropTypes.object,
  hideReset: PropTypes.bool,
  itemId: PropTypes.object,
  layout: PropTypes.array,
  onBlur: PropTypes.func,
  onCancel: PropTypes.func,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onFormClick: PropTypes.func,
  onRemoveItem: PropTypes.func,
  onResetForm: PropTypes.func,
  onRowChange: PropTypes.func,
  onSubmit: PropTypes.func,
  onTagChange: PropTypes.func,
  onWrapperClick: PropTypes.func,
  options: PropTypes.object,
  persistTabs: PropTypes.bool,
  previousStep: PropTypes.func,
  readOnly: PropTypes.bool,
  report: PropTypes.object,
  saving: PropTypes.bool,
  setInfo: PropTypes.func,
  setWarning: PropTypes.func,
  showHelp: PropTypes.bool,
  steps: PropTypes.array,
  submitLabel: PropTypes.string,
  originalRender: PropTypes.bool,

  manager: PropTypes.object.isRequired,
};

export default withFormContext(FormEdit);
